import { IWorkFlow, IWorkFlowBaseInfo, IWorkFlowFolder, LeftPanelType, RightPanelType } from "@kuika/kuika-cl-model";
import { useMemo } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaEntitySwitcher, {
  KuikaEntitySwitcherFolder,
  KuikaEntitySwitcherGroup
} from "../../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { ISwitcherDelete, useSwitcherDelete } from "../../data-diagram/editor-switcher/use-switcher-delete";
import { isProcessActive, processTestIdSlug } from "../create-workflow-processes";
import { buildProcessRowActions, deleteProcessEverywhere, toggleProcessActive } from "../process-row-actions";
import "./process-switcher.scss";

/**
 * The header's process name doubles as the process switcher. It takes over the two jobs the
 * (now hidden) Processes panel did — moving between processes and starting a new one — and opens
 * the panel every switcher in the designer opens: the process folders the Processes panel kept,
 * Active / Inactive chips, the "…" the process list offers a row, and a way back to that list.
 *
 * Its suite ids (`processSwitcherTrigger`, `processSwitcherPanel`, `processSwitcherAdd`,
 * `processSwitcherSearch`, `processSwitcherOption-<slug>`) are the ones the E2E suite resolves.
 */

export interface IProcessSwitcherProps {}

/** A process as the switcher lists it: the list entry, named the way the header names it. */
type IProcessSwitcherItem = IWorkFlowBaseInfo;

const PROCESS_GROUPS: KuikaEntitySwitcherGroup[] = [
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" }
];

const ProcessSwitcher = ({ sd }: IProcessSwitcherProps & IWithSDProps) => {
  const current: IWorkFlow | undefined = sd.selectedWf;

  // While a process is still loading, `selectedWf` isn't committed yet — fall back to the lightweight
  // `workFlowList` entry so the header shows the process name IMMEDIATELY instead of "Select Process"
  // until the full Xml/Json load lands. Prefer the process being loaded RIGHT NOW (`selectingWfId` —
  // set for the very first selection too, before `lastOpenWfId` exists), then the last-opened id.
  const currentId: string | undefined = current?.id || sd.selectingWfId || sd.lastOpenWfId;
  const resolvedName: string | undefined = useMemo((): string | undefined => {
    const fromSelected: string | undefined = current?.displayName || current?.name;
    if (fromSelected) return fromSelected;
    const pending: IWorkFlowBaseInfo | undefined = (sd.workFlowList ?? []).find(
      (wf: IWorkFlowBaseInfo): boolean => wf.id === currentId
    );
    return pending?.displayName || pending?.name;
  }, [current, currentId, sd.workFlowList]);

  const items: IProcessSwitcherItem[] = useMemo(
    (): IProcessSwitcherItem[] =>
      (sd.workFlowList ?? []).map((wf: IWorkFlowBaseInfo): IProcessSwitcherItem => ({
        ...wf,
        name: wf.displayName || wf.name
      })),
    [sd.workFlowList]
  );

  const folders: KuikaEntitySwitcherFolder[] = (sd.project?.workFlowFolders ?? []).map(
    (folder: IWorkFlowFolder): KuikaEntitySwitcherFolder => ({
      id: folder.id,
      name: folder.name,
      itemIds: folder.workFlowIds ?? []
    })
  );

  const backToList = (): void => {
    sd.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowList);
    sd.setCreatingWfActive(true);
    void sd.clearSelectedWf();
  };

  const reloadList = async (): Promise<void> => {
    if (sd.project?.id) await sd.loadWorkFlowList(sd.project.id);
  };

  const { requestDelete, deleteModal }: ISwitcherDelete<IProcessSwitcherItem> = useSwitcherDelete(
    "process",
    (target: IProcessSwitcherItem): void => {
      const wasOpen: boolean = target.id === currentId;
      deleteProcessEverywhere(sd, target)
        .then(reloadList)
        .then((): void => {
          if (wasOpen) backToList();
        })
        .catch(KMainFunctions.exceptionHandler);
    }
  );

  return (
    <>
      <KuikaEntitySwitcher<IProcessSwitcherItem>
        trigger="chip"
        className="kuika-process-switcher__trigger"
        triggerIcon={<KuikaSvg name="tournament" width={16} height={16} color="var(--dark-11)" />}
        triggerLabel={resolvedName || "Select Process"}
        triggerTestId="processSwitcherTrigger"
        panelTestId="processSwitcherPanel"
        searchTestId="processSwitcherSearch"
        createTestId="processSwitcherAdd"
        items={items}
        current={items.find((item: IProcessSwitcherItem): boolean => item.id === currentId)}
        entityLabel="Process"
        entityLabelPlural="Processes"
        iconName="tournament"
        folders={folders}
        groups={PROCESS_GROUPS}
        getItemGroup={(item: IProcessSwitcherItem): string => (isProcessActive(item) ? "active" : "inactive")}
        rowActions={buildProcessRowActions({
          onToggleActive: (process: IProcessSwitcherItem): void => {
            toggleProcessActive(process).then(reloadList).catch(KMainFunctions.exceptionHandler);
          },
          onRequestDelete: requestDelete
        })}
        itemTestId={(item: IProcessSwitcherItem): string => `processSwitcherOption-${processTestIdSlug(item.name)}`}
        onSelect={(item: IProcessSwitcherItem): void => {
          sd.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowFieldList);
          sd.setActiveWorkFlowRightPanel(RightPanelType.Properties);
          sd.setSelectedWf(item.id);
        }}
        // Returning to the gallery is how a new process gets created.
        onCreateNew={(): void => {
          void sd.clearSelectedWf();
          sd.setCreatingWfActive(true);
        }}
        onManageAll={backToList}
      />
      {deleteModal}
    </>
  );
};

const processSwitcher = withSD<IProcessSwitcherProps>(ProcessSwitcher);
export { processSwitcher as ProcessSwitcher };
